import { ScrollingModule } from '@angular/cdk/scrolling'
import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ActivatedRoute } from '@angular/router'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators'
import { Invitation, InvitationCounts, InvitationStatus } from '../../models/invitation-tracking.interface'
import { InvitationsService } from '../../services/invitations.service'

// Status filter configuration
const STATUS_FILTERS = [
  { key: 'all' as InvitationStatus, label: 'All' },
  { key: 'delivered' as InvitationStatus, label: 'Delivered' },
  { key: 'accepted' as InvitationStatus, label: 'Accepted' },
  { key: 'declined' as InvitationStatus, label: 'Declined' },
  { key: 'bounced' as InvitationStatus, label: 'Bounced' },
  { key: 'reported' as InvitationStatus, label: 'Reported' },
  { key: 'invalid' as InvitationStatus, label: 'Invalid' }
] as const

/**
 * Displays invitations in a table with filtering, searching and selection capabilities
 */
@Component({
  selector: 'ngx-invitations-tracking',
  templateUrl: './invitations-tracking.component.html',
  styleUrls: ['./invitations-tracking.component.scss'],
  imports: [CommonModule, MatTableModule, MatIconModule, MatTooltipModule, ScrollingModule],
  standalone: true
})
export class InvitationsTrackingComponent implements OnInit, OnDestroy {
  eventId: string

  // Column definitions for the table
  displayedColumns: string[] = ['fullname', 'email', 'from_event_name', 'delivered', 'registration', 'declined', 'bounced', 'reported', 'invalid']

  // Data source for the material table
  dataSource = new MatTableDataSource<Invitation>([])

  // Loading state
  isLoading = false

  // Status filters with counts and percentages
  public statusFilters = STATUS_FILTERS.map((filter) => ({
    ...filter,
    count: 0,
    percentage: '0%'
  }))

  // Currently active filter
  public currentStatus: InvitationStatus = 'all'

  // Search term
  public searchTerm = ''

  // Store the counts from the API
  private _invitationCounts: InvitationCounts = {
    all: 0,
    delivered: 0,
    accepted: 0,
    declined: 0,
    bounced: 0,
    reported: 0,
    invalid: 0
  }

  // Getter for template to access data
  public get invitations(): Invitation[] {
    return this.dataSource.data
  }

  // Subject for handling component destruction
  private readonly _destroy$ = new Subject<void>()

  // Subject for search term changes
  private readonly _searchSubject = new Subject<string>()

  // Pagination settings
  private _currentPage = 1
  private _totalItems = 0
  private readonly _pageSize = 50

  // Track initial load state
  private _initialLoad = true

  constructor(
    private readonly _invitationsService: InvitationsService,
    private readonly _route: ActivatedRoute,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone
  ) {
    // Initialize search debounce
    this._searchSubject.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._destroy$)).subscribe((searchTerm) => {
      this.searchTerm = searchTerm
      this._currentPage = 1 // Reset to first page on new search
      this._initialLoad = true
      this.loadInvitations()
    })
  }

  /**
   * Initialize component and load initial data if eventId is available
   */
  public ngOnInit(): void {
    this.eventId = this._route.snapshot.params['id']
    this._initializeData()
  }

  /**
   * Clean up subscriptions on component destruction
   */
  public ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Initialize component data
   */
  private _initializeData(): void {
    // Reset pagination and filters
    this.searchTerm = ''
    this.currentStatus = 'all'
    this._totalItems = 0

    if (this.eventId) {
      // First get the counts
      this._invitationsService
        .getInvitationCounts(this.eventId)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (counts) => {
            this._invitationCounts = counts
            this._updateStatusCounts()
            // Then load the invitations
            this.loadInvitations()
          },
          error: (error) => {
            console.error('Error loading invitation counts:', error)
            this.loadInvitations()
          }
        })
    } else {
      console.warn('EventId is required for InvitationsTrackingComponent')
      this.dataSource.data = []
      this._updateStatusCounts()
    }
  }

  /**
   * Load invitations from the service with pagination
   */
  public loadInvitations(): void {
    if (this.isLoading) return

    this.isLoading = true

    this._invitationsService
      .getInvitations(this.eventId, this.searchTerm || '', this._currentPage, this._pageSize, this.currentStatus === 'all' ? undefined : this.currentStatus)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false
          this._initialLoad = false
          this._cdr.detectChanges()
        })
      )
      .subscribe({
        next: (response) => {
          const filteredData = response.data.filter((item) => item.status_text?.toLowerCase() !== 'pending')

          // Update total items based on API response
          this._totalItems = response.total

          // Update data source
          if (this._initialLoad || this._currentPage === 1) {
            this.dataSource.data = filteredData
          } else {
            this.dataSource.data = [...this.dataSource.data, ...filteredData]
          }

          // Only increment page after successful load
          if (!this._initialLoad && this._currentPage === 1) {
            this._currentPage++
          }
        },
        error: () => {
          this.dataSource.data = []
        }
      })
  }

  /**
   * Update status counts and percentages
   */
  private _updateStatusCounts(): void {
    this.statusFilters.forEach((filter) => {
      const count = this.getStatusCount(filter.key)
      filter.count = count
      filter.percentage = this.getStatusPercentage(filter.key)
    })
  }

  /**
   * Apply status filter to the table
   */
  public filterByStatus(status: InvitationStatus): void {
    if (status !== this.currentStatus) {
      this.currentStatus = status
      this._currentPage = 1
      this._initialLoad = true
      this.loadInvitations()
    }
  }

  /**
   * Handle search input with debounce
   */
  public onSearch(event: Event): void {
    const input = event.target as HTMLInputElement
    const searchTerm = input.value.trim()
    this._searchSubject.next(searchTerm)
  }

  /**
   * Get count for a specific status
   */
  public getStatusCount(status: InvitationStatus): number {
    return Number(this._invitationCounts[status]) || 0
  }

  /**
   * Get total count of invitations
   */
  public getTotalCount(): number {
    return Number(this._invitationCounts.all) || 0
  }

  /**
   * Get percentage for specific status
   */
  public getStatusPercentage(status: InvitationStatus): string {
    const total = this.getTotalCount()
    const count = this.getStatusCount(status)
    const percentage = total > 0 ? Math.round((count / total) * 100) : 0
    return `${percentage}%`
  }

  /**
   * Handle scroll events for infinite loading
   */
  public onScroll(event: any): void {
    if (this._initialLoad || this.isLoading) return

    const viewport = event.currentTarget
    const scrollHeight = viewport.scrollHeight
    const scrollTop = viewport.scrollTop
    const clientHeight = viewport.clientHeight

    // Check if we're near the bottom (within 200px)
    if (scrollHeight - (scrollTop + clientHeight) < 200) {
      // Only load more if we haven't already loaded all data
      if (this.dataSource.data.length < this._totalItems) {
        this._currentPage++
        this.loadInvitations()
      }
    }
  }
}
